/**
 * Ruban LED de la borne, piloté par un ESP32 branché en USB (voir `firmware/`).
 *
 * Les couleurs se règlent dans l'admin distante : elles voyagent avec les
 * réglages client (`AdminSettings.leds`). Le firmware n'en connaît aucune — la
 * borne lui envoie des couleurs déjà prêtes, luminosité comprise, comme le
 * bandeau EXAIL RESA.
 *
 * Fichier partagé à l'identique avec le dépôt de l'admin — voir `cloud.ts`.
 */

/** Sens du déroulé à chaque seconde : depuis la première LED, la dernière, ou le milieu. */
export type WipeDirection = 'start' | 'end' | 'center';

export interface LedSettings {
  enabled: boolean;
  /** Couleur au repos, entre deux photos. `#000000` = ruban éteint. */
  idleColor: string;
  /**
   * Une couleur par seconde du décompte, alignées sur la fin : la dernière est
   * celle du « 1 ». Un décompte plus long que la liste reprend la première.
   */
  countdownColors: string[];
  /** Couleur que la seconde suivante recouvre en se déroulant. */
  countdownBackground: string;
  wipeDirection: WipeDirection;
  /** Durée du déroulé, dans la seconde. Au-delà de 950 ms il ne finirait pas avant la suivante. */
  wipeMs: number;
  flashColor: string;
  /** 0 à 1 — repos et décompte. */
  brightness: number;
  /** 0 à 1 — le flash seul, pour éclairer les visages sans éblouir au repos. */
  flashBrightness: number;
  /**
   * Plage d'allumage, à l'heure de la borne. Hors de la plage — la nuit — le
   * ruban reste éteint au repos ; une photo prise quand même garde son
   * décompte et son flash.
   */
  schedule: LedSchedule;
}

export interface LedSchedule {
  enabled: boolean;
  /** « HH:MM » : allumage. */
  on: string;
  /** « HH:MM » : extinction. Plus tôt que `on`, la plage passe minuit (18:00 → 02:00). */
  off: string;
}

export const DEFAULT_LED_SETTINGS: LedSettings = {
  enabled: true,
  idleColor: '#14286E',
  countdownColors: ['#2F6BFF', '#FFB020', '#FF3B30'],
  countdownBackground: '#000000',
  wipeDirection: 'center',
  wipeMs: 700,
  flashColor: '#FFFFFF',
  brightness: 0.6,
  flashBrightness: 1,
  schedule: { enabled: false, on: '08:00', off: '20:00' },
};

export const MAX_COUNTDOWN_COLORS = 6;

const HEX = /^#[0-9a-f]{6}$/i;

/** Bornes appliquées par l'admin à l'écriture, puis par la borne à la réception. */
export function sanitizeLedSettings(value: Partial<LedSettings> | undefined): LedSettings {
  const d = DEFAULT_LED_SETTINGS;
  const v = value ?? {};
  const color = (c: unknown, fallback: string) =>
    typeof c === 'string' && HEX.test(c) ? c.toUpperCase() : fallback;
  const ratio = (n: unknown, fallback: number) =>
    Number.isFinite(Number(n)) ? Math.min(1, Math.max(0, Number(n))) : fallback;

  const colors = (Array.isArray(v.countdownColors) ? v.countdownColors : [])
    .filter((c): c is string => typeof c === 'string' && HEX.test(c))
    .slice(0, MAX_COUNTDOWN_COLORS)
    .map((c) => c.toUpperCase());

  const wipeMs = Number(v.wipeMs);
  const time = (t: unknown, fallback: string) =>
    typeof t === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(t) ? t : fallback;

  return {
    enabled: v.enabled === undefined ? d.enabled : Boolean(v.enabled),
    idleColor: color(v.idleColor, d.idleColor),
    countdownColors: colors.length ? colors : [...d.countdownColors],
    countdownBackground: color(v.countdownBackground, d.countdownBackground),
    wipeDirection: v.wipeDirection === 'start' || v.wipeDirection === 'end' ? v.wipeDirection : 'center',
    wipeMs: Number.isFinite(wipeMs) ? Math.min(950, Math.max(150, Math.round(wipeMs))) : d.wipeMs,
    flashColor: color(v.flashColor, d.flashColor),
    brightness: ratio(v.brightness, d.brightness),
    flashBrightness: ratio(v.flashBrightness, d.flashBrightness),
    schedule: {
      enabled: Boolean(v.schedule?.enabled),
      on: time(v.schedule?.on, d.schedule.on),
      off: time(v.schedule?.off, d.schedule.off),
    },
  };
}

const minutes = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/** Le ruban doit-il être allumé au repos à cette heure ? Heure locale de la machine. */
export function ledsOnAt(schedule: LedSchedule, date: Date): boolean {
  if (!schedule.enabled) return true;
  const now = date.getHours() * 60 + date.getMinutes();
  const on = minutes(schedule.on);
  const off = minutes(schedule.off);
  if (on === off) return true;
  return on < off ? now >= on && now < off : now >= on || now < off;
}

/** Couleur de la seconde `remaining` (3, 2, 1…), alignée sur la fin de la liste. */
export function countdownColorFor(settings: LedSettings, remaining: number): string {
  const colors = settings.countdownColors;
  const index = colors.length - Math.max(1, Math.round(remaining));
  return colors[Math.max(0, index)] ?? DEFAULT_LED_SETTINGS.countdownColors[0];
}

/** `#RRGGBB` multiplié par `factor`, rendu en `RRGGBB` — le format du firmware. */
export function scaleColor(hex: string, factor: number): string {
  const n = parseInt(hex.slice(1), 16);
  const f = Math.min(1, Math.max(0, factor));
  const channel = (shift: number) => Math.round(((n >> shift) & 0xff) * f);
  return ((channel(16) << 16) | (channel(8) << 8) | channel(0))
    .toString(16)
    .toUpperCase()
    .padStart(6, '0');
}

/** État du contrôleur, publié par la borne avec son battement. */
export interface LedStatus {
  connected: boolean;
  /** Port série ouvert, ou vide. */
  port: string;
  /** Version annoncée par le firmware. */
  firmware: string;
  /** Explication lisible quand le contrôleur n'est pas là. */
  detail: string;
  /** Configuration du ruban lue sur l'ESP32 (enregistrée dans sa mémoire). */
  strip?: { nleds: number; pin: string; maxBrightness: number };
}
